import axios from 'axios'
import React, {useEffect, useRef, useState} from 'react'
import {FileUploader} from 'react-drag-drop-files'
import LoadingOverlay from 'react-loading-overlay'

import './Productos.css'

export function Imagenes() {
  const API_URL = process.env.REACT_APP_API_URL
  const [spinnerIsActive, setSpinnerIsActive] = useState(false)
  const [showAlertImages, setShowAlertImages] = useState(false)
  const [actionAlert, setActionAlert] = useState(null)
  const [uploaderKey, setUploaderKey] = useState(0)
  const [pendingImages, setPendingImages] = useState([])
  const [imageValidationMessages, setImageValidationMessages] = useState([])
  const [imageUploadSummary, setImageUploadSummary] = useState(null)
  const [productCode, setProductCode] = useState('')
  const [searchedCode, setSearchedCode] = useState('')
  const [productImages, setProductImages] = useState([])
  const [imageSearchStatus, setImageSearchStatus] = useState('idle')
  const [imageSearchMessage, setImageSearchMessage] = useState(null)
  const previewUrls = useRef([])

  useEffect(() => () => previewUrls.current.forEach((url) => URL.revokeObjectURL(url)), [])

  useEffect(() => {
    if (!showAlertImages) return undefined
    const timer = window.setTimeout(() => setShowAlertImages(false), 5000)
    return () => window.clearTimeout(timer)
  }, [showAlertImages])

  useEffect(() => {
    if (!actionAlert) return undefined
    const timer = window.setTimeout(() => setActionAlert(null), 5000)
    return () => window.clearTimeout(timer)
  }, [actionAlert])

  useEffect(() => {
    if (imageValidationMessages.length === 0) return undefined
    const timer = window.setTimeout(() => setImageValidationMessages([]), 5000)
    return () => window.clearTimeout(timer)
  }, [imageValidationMessages])

  useEffect(() => {
    if (!imageSearchMessage) return undefined
    const timer = window.setTimeout(() => setImageSearchMessage(null), 5000)
    return () => window.clearTimeout(timer)
  }, [imageSearchMessage])

  const validateAndPreviewImages = (selectedFiles) => {
    const incomingFiles = Array.from(selectedFiles || [])
    const allowedExtensions = ['png', 'jpg', 'jpeg', 'gif']
    const maximumFileSize = 600 * 1024
    const messages = []
    const acceptedImages = []
    const existingFiles = new Set(
      pendingImages.map(({file}) => `${file.name}-${file.size}-${file.lastModified}`)
    )

    incomingFiles.forEach((file) => {
      const extension = file.name.split('.').pop().toLowerCase()
      const fileKey = `${file.name}-${file.size}-${file.lastModified}`

      if (!allowedExtensions.includes(extension)) {
        messages.push(`${file.name}: formato no permitido.`)
        return
      }
      if (file.size > maximumFileSize) {
        messages.push(`${file.name}: excede el peso máximo de 600kb.`)
        return
      }
      if (existingFiles.has(fileKey)) {
        messages.push(`${file.name}: el archivo ya está seleccionado.`)
        return
      }
      if (pendingImages.length + acceptedImages.length >= 100) {
        messages.push(`${file.name}: se alcanzó el máximo de 100 imágenes.`)
        return
      }

      const previewUrl = URL.createObjectURL(file)
      previewUrls.current.push(previewUrl)
      existingFiles.add(fileKey)
      acceptedImages.push({
        id: `${fileKey}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl,
      })
    })

    setPendingImages((currentImages) => [...currentImages, ...acceptedImages])
    setImageValidationMessages(messages)
  }

  const removePendingImage = (imageId) => {
    setPendingImages((currentImages) => {
      const image = currentImages.find((item) => item.id === imageId)
      if (image) {
        URL.revokeObjectURL(image.previewUrl)
        previewUrls.current = previewUrls.current.filter((url) => url !== image.previewUrl)
      }
      return currentImages.filter((item) => item.id !== imageId)
    })
  }

  const uploadImages = async () => {
    if (pendingImages.length === 0) return

    const imagesToUpload = [...pendingImages]
    const uploadedImages = []
    const failedImages = []
    setSpinnerIsActive(true)
    setImageUploadSummary(null)

    for (let start = 0; start < imagesToUpload.length; start += 10) {
      const batch = imagesToUpload.slice(start, start + 10)
      const data = new FormData()
      batch.forEach(({file}) => data.append('images[]', file))

      try {
        const response = await axios.post(API_URL + '/carga-imagenes', data)
        const result = response.data || {}
        const batchUploaded = Array.isArray(result.uploadedImages) ? result.uploadedImages : []
        const batchFailed = Array.isArray(result.failedImages) ? result.failedImages : []
        const reportedNames = new Set([
          ...batchUploaded,
          ...batchFailed.map((failure) => failure.name),
        ])
        uploadedImages.push(...batchUploaded)
        failedImages.push(...batchFailed)
        batch.forEach(({file}) => {
          if (!reportedNames.has(file.name))
            failedImages.push({name: file.name, reason: 'El servidor no procesó el archivo.'})
        })
      } catch (error) {
        const status = error.response?.status
        let reason = error.response?.data?.message || 'No fue posible procesar el lote.'
        if (status === 413) reason = 'El lote excedió el tamaño permitido por el servidor.'
        if (status === 429) reason = 'El servidor limitó temporalmente las solicitudes.'
        if (status === 500) reason = 'El servidor encontró un error al procesar el lote.'
        if (error.code === 'ECONNABORTED') reason = 'La solicitud excedió el tiempo de espera.'
        batch.forEach(({file}) => failedImages.push({name: file.name, reason}))
      }
    }

    const failedNames = new Set(failedImages.map((failure) => failure.name))
    const remainingImages = imagesToUpload.filter(({file}) => failedNames.has(file.name))
    imagesToUpload
      .filter(({file}) => !failedNames.has(file.name))
      .forEach(({previewUrl}) => URL.revokeObjectURL(previewUrl))
    previewUrls.current = remainingImages.map(({previewUrl}) => previewUrl)
    setPendingImages(remainingImages)
    setUploaderKey((previousKey) => previousKey + 1)
    setImageUploadSummary({
      total: imagesToUpload.length,
      uploaded: uploadedImages.length,
      failed: failedImages.length,
      failedImages,
    })
    setSpinnerIsActive(false)
    setShowAlertImages(Date.now())
  }

  const searchProductImages = () => {
    const code = productCode.trim()
    if (!code) {
      setSearchedCode('')
      setProductImages([])
      setImageSearchStatus('error')
      setImageSearchMessage({text: 'Ingresa el código del producto.', id: Date.now()})
      return
    }

    setSpinnerIsActive(true)
    setImageSearchMessage(null)
    axios.get(`${API_URL}/producto-codigo/${encodeURIComponent(code)}`).then(
      (response) => {
        const product = response.data.data
        setSpinnerIsActive(false)
        setSearchedCode(code)
        if (!product || !product.id) {
          setProductImages([])
          setImageSearchStatus('not-found')
          setImageSearchMessage({
            text: 'No se encontró ningún producto con ese código.',
            id: Date.now(),
          })
          return
        }
        const images = Array.isArray(product.imagenes) ? product.imagenes : []
        setProductImages(images)
        setImageSearchStatus(images.length > 0 ? 'success' : 'empty')
        setImageSearchMessage(
          images.length > 0
            ? null
            : {text: 'Este producto no tiene imágenes registradas.', id: Date.now()}
        )
      },
      () => {
        setSpinnerIsActive(false)
        setSearchedCode(code)
        setProductImages([])
        setImageSearchStatus('error')
        setImageSearchMessage({
          text: 'No fue posible consultar las imágenes del producto.',
          id: Date.now(),
        })
      }
    )
  }

  const deleteProductImage = (image) => {
    if (!window.confirm(`¿Deseas eliminar la imagen ${image.nombre || ''}?`)) return
    setSpinnerIsActive(true)
    axios.delete(`${API_URL}/eliminar-imagen-producto/${image.id}`).then(
      () => {
        const remainingImages = productImages.filter((item) => item.id !== image.id)
        setProductImages(remainingImages)
        setImageSearchStatus(remainingImages.length > 0 ? 'success' : 'empty')
        setImageSearchMessage(
          remainingImages.length > 0
            ? null
            : {text: 'Este producto no tiene imágenes registradas.', id: Date.now()}
        )
        setSpinnerIsActive(false)
      },
      (error) => {
        setSpinnerIsActive(false)
        setActionAlert({
          type: 'danger',
          message: error.response?.data?.message || 'No fue posible eliminar la imagen.',
        })
      }
    )
  }

  return (
    <>
      <LoadingOverlay active={spinnerIsActive} spinner text='Cargando...'>
        <div className='productos-management'>
          <h1 className='mb-0'>Imágenes</h1>
          <div className='card card-custom shadow productos-card'>
            <div className='card-header productos-card-header'>
              <div>
                <h3 className='card-title'>Cargar imágenes</h3>
                <p className='productos-card-description'>
                  Carga nuevas imágenes para uno o varios productos.
                </p>
              </div>
            </div>
            <div className='card-body'>
              <div className='form-group productos-field productos-field-full'>
                <label className='mb-2'>Archivo de imágenes:</label>
                <FileUploader
                  key={uploaderKey}
                  multiple={true}
                  handleChange={validateAndPreviewImages}
                  name='file'
                  label='Sube o arrastra un archivo aquí mismo.'
                  uploadedLabel='Archivos listos para subir'
                  classes='drop_zone'
                  hoverTitle='Soltar aquí'
                />
              </div>
              <p className='productos-restrictions'>
                Formatos permitidos: PNG, JPG, JPEG, GIF · Máximo 100 imágenes · 600kb por imagen.
              </p>
              {imageValidationMessages.length > 0 && (
                <div className='productos-image-validation' role='alert'>
                  {imageValidationMessages.map((message, index) => (
                    <p key={`${message}-${index}`}>{message}</p>
                  ))}
                </div>
              )}
              {pendingImages.length > 0 && (
                <div className='productos-pending-images'>
                  <h4>
                    {pendingImages.length}{' '}
                    {pendingImages.length === 1 ? 'imagen seleccionada' : 'imágenes seleccionadas'}
                  </h4>
                  <div className='productos-pending-grid'>
                    {pendingImages.map((image) => (
                      <article className='producto-pending-card' key={image.id}>
                        <button
                          type='button'
                          className='producto-pending-remove'
                          aria-label={`Quitar ${image.file.name}`}
                          title='Quitar'
                          onClick={() => removePendingImage(image.id)}
                        >
                          ×
                        </button>
                        <div className='producto-pending-preview'>
                          <img src={image.previewUrl} alt={image.file.name} />
                        </div>
                        <span title={image.file.name}>{image.file.name}</span>
                      </article>
                    ))}
                  </div>
                </div>
              )}
              <div className='productos-button-row'>
                <button
                  type='button'
                  className='btn btn-primary'
                  disabled={pendingImages.length === 0}
                  onClick={uploadImages}
                >
                  Cargar imágenes
                </button>
              </div>
            </div>
          </div>

          <div className='card card-custom shadow productos-card'>
            <div className='card-header productos-card-header'>
              <div>
                <h3 className='card-title'>Eliminar imágenes</h3>
                <p className='productos-card-description'>
                  Busca un producto por su código para consultar y eliminar sus imágenes.
                </p>
              </div>
            </div>
            <div className='card-body'>
              <div className='productos-action-row'>
                <div className='form-group productos-field'>
                  <label htmlFor='codigo-producto-imagenes'>Código del producto</label>
                  <input
                    id='codigo-producto-imagenes'
                    type='text'
                    className='form-control'
                    value={productCode}
                    placeholder='Ingresa el código del producto'
                    onChange={(event) => setProductCode(event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && searchProductImages()}
                  />
                </div>
                <button
                  type='button'
                  className='btn btn-primary productos-action-button'
                  onClick={searchProductImages}
                >
                  Buscar
                </button>
              </div>
              {imageSearchMessage && (
                <div className='productos-search-message' role='status'>
                  {imageSearchMessage.text}
                </div>
              )}
              {imageSearchStatus === 'success' && (
                <div className='productos-images-result'>
                  <h4>Imágenes del producto ({searchedCode})</h4>
                  <div className='productos-images-grid'>
                    {productImages.map((image) => (
                      <article className='producto-image-card' key={image.id}>
                        <div className='producto-image-preview'>
                          <img src={image.url} alt={image.nombre || `Imagen de ${searchedCode}`} />
                        </div>
                        <div className='producto-image-info'>
                          <span title={image.nombre}>{image.nombre || 'Imagen de producto'}</span>
                          <button
                            type='button'
                            className='btn btn-sm producto-image-delete'
                            onClick={() => deleteProductImage(image)}
                          >
                            Eliminar
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                  <p className='productos-images-count'>
                    Mostrando {productImages.length} de {productImages.length} imágenes
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </LoadingOverlay>

      {showAlertImages && (
        <div className='productos-page-alert alert alert-dismissible bg-primary d-flex flex-column flex-sm-row p-5 mb-10'>
          <div className='d-flex flex-column text-dark pe-0 pe-sm-10'>
            <h5 className='mb-1'>Carga de imágenes finalizada</h5>
            {imageUploadSummary && (
              <>
                <span>Seleccionadas: {imageUploadSummary.total}</span>
                <span>Cargadas correctamente: {imageUploadSummary.uploaded}</span>
                <span>Fallidas: {imageUploadSummary.failed}</span>
                {imageUploadSummary.failedImages.length > 0 && (
                  <ul className='mb-0 mt-2 ps-5'>
                    {imageUploadSummary.failedImages.map((failure, index) => (
                      <li key={`${failure.name}-${index}`}>
                        {failure.name}: {failure.reason}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          <button
            type='button'
            className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
            onClick={() => setShowAlertImages(false)}
          >
            <span className='svg-icon svg-icon-2x svg-icon-light icon-close'>&#215;</span>
          </button>
        </div>
      )}

      {actionAlert && (
        <div
          className={`productos-page-alert alert alert-dismissible bg-${actionAlert.type} d-flex flex-column flex-sm-row p-5 mb-10`}
          role='alert'
        >
          <div className='d-flex flex-column text-dark pe-0 pe-sm-10'>
            <h5 className='mb-1'>Aviso</h5>
            <span>{actionAlert.message}</span>
          </div>
          <button
            type='button'
            className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
            onClick={() => setActionAlert(null)}
          >
            <span className='svg-icon svg-icon-2x svg-icon-light icon-close'>&#215;</span>
          </button>
        </div>
      )}
    </>
  )
}
